/**
 * F-09 (2026-05-13 UAT batch Phase 3) — shared currency-aware rate input.
 *
 * Wraps a numeric rate input with a sibling currency dropdown + an
 * on-demand "Convert to CZK" button. Storage is always CZK — the parent
 * receives the final CZK value via `onChange`. Mirrors the pattern
 * inlined in `components/domain/AssignmentForm.tsx` (which predates
 * this component); extracted here so the JR Approve modal,
 * ProjectFormPage, and ProjectEditModal don't have to duplicate the
 * exchange-rate fetch + conversion plumbing.
 *
 * Behaviour contract:
 *   - Initial state: currency='CZK', value passes through unchanged.
 *   - When the user picks a foreign currency, the input continues to
 *     accept the foreign-currency amount but `onChange` does NOT fire
 *     until the user clicks Convert. This prevents committing a
 *     pre-conversion number as if it were CZK.
 *   - The Convert button calls `getExchangeRates` (backend proxy of
 *     ČNB) for today, multiplies via `convertToCZK`, and emits the
 *     resulting CZK number. The displayed conversion summary (rate +
 *     "= X CZK") stays visible until the user changes amount or
 *     currency, so the operator sees what they're committing.
 *
 * The `requiresConversion` ref / value pair lets parents block
 * submission while a foreign-currency value is staged but not yet
 * converted (mirrors AssignmentForm's `if (currency !== 'CZK' &&
 * billingRate && !convertedBillingRate)` guard).
 */
import { useState } from 'react';
import { getExchangeRates, convertToCZK, COMMON_CURRENCIES } from '../../services/exchangeRate';

interface CurrencyRateInputProps {
  /** Field label (e.g. "Billing Rate", "Default Rate"). */
  label: string;
  /** Current CZK value (or empty string for cleared state). */
  value: string;
  /** Called with the CZK string once the user commits a value. */
  onChange: (czkValue: string) => void;
  placeholder?: string;
  /** Optional id for the input — useful for test selectors. */
  inputId?: string;
}

export function CurrencyRateInput({
  label,
  value,
  onChange,
  placeholder = '0.00',
  inputId,
}: CurrencyRateInputProps) {
  const [currency, setCurrency] = useState('CZK');
  // Local draft amount in the SELECTED currency. When CZK is selected
  // this is the same as `value`; for foreign currencies the parent's
  // `value` lags behind until Convert runs.
  const [draftAmount, setDraftAmount] = useState(value);
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [convertedCzk, setConvertedCzk] = useState<number | null>(null);
  const [loadingRate, setLoadingRate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ccyInfo = COMMON_CURRENCIES.find((c) => c.code === currency);
  const inputLabel =
    currency === 'CZK' ? 'CZK/h' : `${ccyInfo?.symbol ?? currency}/h`;

  const handleAmountChange = (next: string) => {
    setDraftAmount(next);
    setConvertedCzk(null);
    setError(null);
    if (currency === 'CZK') {
      onChange(next);
    }
  };

  const handleCurrencyChange = (nextCurrency: string) => {
    setCurrency(nextCurrency);
    setExchangeRate(null);
    setConvertedCzk(null);
    setError(null);
    // Switching back to CZK promotes the draft to a committed value.
    if (nextCurrency === 'CZK') {
      onChange(draftAmount);
    } else {
      // Switching to foreign — clear committed CZK so the parent's
      // submit-time guard (e.g. handleApprove) can refuse a
      // pre-conversion value.
      onChange('');
    }
  };

  const handleConvert = async () => {
    if (!draftAmount) return;
    setLoadingRate(true);
    setError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const rates = await getExchangeRates(today);
      const rate = rates.get(currency);
      if (!rate) {
        setError(`Exchange rate not found for ${currency}`);
        return;
      }
      setExchangeRate(rate);
      const czk = convertToCZK(parseFloat(draftAmount), currency, rate);
      const rounded = czk.toFixed(2);
      setConvertedCzk(czk);
      onChange(rounded);
    } catch {
      setError('Failed to fetch exchange rates. Please try again.');
    } finally {
      setLoadingRate(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Currency
          </label>
          <select
            value={currency}
            onChange={(e) => handleCurrencyChange(e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {COMMON_CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} ({c.symbol})
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <label
            htmlFor={inputId}
            className="block text-xs font-medium text-gray-600 mb-1"
          >
            {label} ({inputLabel})
          </label>
          <input
            id={inputId}
            type="number"
            step="0.01"
            min="0"
            value={draftAmount}
            onChange={(e) => handleAmountChange(e.target.value)}
            placeholder={placeholder}
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {currency !== 'CZK' && (
        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="text-gray-700">Convert to CZK to commit value</span>
            <button
              type="button"
              onClick={handleConvert}
              disabled={!draftAmount || loadingRate}
              className="px-3 py-1 rounded bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 disabled:opacity-50"
            >
              {loadingRate ? 'Converting…' : 'Convert to CZK'}
            </button>
          </div>
          {exchangeRate !== null && convertedCzk !== null && (
            <div className="mt-1 text-gray-700">
              1 {currency} = {exchangeRate} CZK · committed value:{' '}
              <strong>{convertedCzk.toFixed(2)} CZK</strong>
            </div>
          )}
          {error && <div className="mt-1 text-red-600">{error}</div>}
        </div>
      )}
    </div>
  );
}

/**
 * Phase 72 (currency-aware budgets) — persist-currency + provisional-preview
 * amount input for the three budget/amount fields (Default money budget,
 * SLA Balíček amount, Fixed Price contract amount).
 *
 * Unlike `CurrencyRateInput` above (which converts the entered amount to CZK
 * and discards the original currency — the anti-pattern Phase 70-05 removed
 * from AssignmentForm), this component PERSISTS the amount + currency
 * unconverted: the parent submits `{amount, currency}` as-is, and the backend
 * resolves the CZK equivalent at read/lock time (see `budgetLockService`,
 * 72-02). A display-only "≈ X CZK (provisional)" preview is fetched on
 * demand — on amount-field blur — from the same `/api/exchange-rates` (ČNB)
 * proxy used by `CurrencyRateInput` above; it is never committed, only shown.
 *
 * Restricted to CZK/EUR/USD (CUR-07) to match the backend's validated enum.
 *
 * Renders as flat grid-item siblings (an amount cell + a currency cell, plus
 * a full-width preview row for foreign currencies) — embed directly inside
 * the parent's own `grid` container rather than wrapping in another div, so
 * it lines up with sibling fields (e.g. a "Budget (Hours)" cell) laid out in
 * the same row.
 */
export type PersistCurrencyCode = 'CZK' | 'EUR' | 'USD';

const PERSIST_CURRENCIES: PersistCurrencyCode[] = ['CZK', 'EUR', 'USD'];

export interface PersistCurrencyAmountInputProps {
  /** Amount field label (e.g. "Budget", "Amount / month", "Contract amount"). */
  label: string;
  /** Raw amount, unconverted — persisted as-entered alongside `currency`. */
  amount: string;
  /**
   * '' is only valid when `allowUnset` is true (e.g. the Fixed Price
   * contract-amount field, which forces an explicit pick with no silent
   * CZK default) — renders a "Select currency…" placeholder option.
   */
  currency: PersistCurrencyCode | '';
  onAmountChange: (amount: string) => void;
  onCurrencyChange: (currency: PersistCurrencyCode) => void;
  placeholder?: string;
  amountInputId?: string;
  currencySelectId?: string;
  required?: boolean;
  /** Renders a blank "Select currency…" option when `currency === ''`. */
  allowUnset?: boolean;
}

export function PersistCurrencyAmountInput({
  label,
  amount,
  currency,
  onAmountChange,
  onCurrencyChange,
  placeholder = '0.00',
  amountInputId,
  currencySelectId,
  required = false,
  allowUnset = false,
}: PersistCurrencyAmountInputProps) {
  const [preview, setPreview] = useState<{ rate: number; czk: number } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const fetchPreview = async () => {
    if (currency === 'CZK' || currency === '' || !amount) return;
    setLoadingPreview(true);
    setPreviewError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const rates = await getExchangeRates(today);
      const rate = rates.get(currency);
      if (!rate) {
        setPreviewError(`Exchange rate not found for ${currency}`);
        return;
      }
      setPreview({ rate, czk: convertToCZK(parseFloat(amount), currency, rate) });
    } catch {
      setPreviewError('Failed to fetch exchange rates. Please try again.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleAmountChange = (next: string) => {
    setPreview(null);
    setPreviewError(null);
    onAmountChange(next);
  };

  const handleCurrencyChange = (next: PersistCurrencyCode) => {
    setPreview(null);
    setPreviewError(null);
    onCurrencyChange(next);
  };

  return (
    <>
      <div>
        <label htmlFor={amountInputId} className="block text-xs font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
          id={amountInputId}
          type="text"
          inputMode="decimal"
          value={amount}
          onChange={(e) => handleAmountChange(e.target.value)}
          onBlur={fetchPreview}
          placeholder={placeholder}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          required={required}
        />
      </div>
      <div>
        <label htmlFor={currencySelectId} className="block text-xs font-medium text-gray-700 mb-1">
          Currency {required && <span className="text-red-500">*</span>}
        </label>
        <select
          id={currencySelectId}
          value={currency}
          onChange={(e) => handleCurrencyChange(e.target.value as PersistCurrencyCode)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          required={required}
        >
          {allowUnset && currency === '' && <option value="">Select currency…</option>}
          {PERSIST_CURRENCIES.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
      </div>
      {currency !== 'CZK' && currency !== '' && (
        <div className="col-span-full text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          {loadingPreview && <span>Fetching exchange rate…</span>}
          {!loadingPreview && preview && (
            <span>
              1 {currency} = {preview.rate} CZK · ≈{' '}
              <strong>{preview.czk.toFixed(2)} CZK (provisional)</strong>
            </span>
          )}
          {!loadingPreview && previewError && <span className="text-red-600">{previewError}</span>}
          {!loadingPreview && !preview && !previewError && (
            <span className="text-gray-500">
              Amount and currency are saved as entered — the CZK equivalent is resolved
              automatically (provisional while the period is open, locked once it closes).
              Leave the amount field to preview it now.
            </span>
          )}
        </div>
      )}
    </>
  );
}
